/*
 * iPhone and iPad play HLS natively, so podcasts and videos there use a plain media
 * element. MKPlayer has its own setup before play, and users reported waits of about a
 * minute on reopening a podcast. A plain element has no such setup, and play() runs
 * directly from the tap.
 *
 * Android WebView also claims HLS support through canPlayType, but MKPlayer is the
 * supported path there, so this checks for Apple devices rather than for the format.
 */

export const supportsNativeHls = () => {
  const {userAgent, platform, maxTouchPoints} = navigator;
  // iPadOS reports itself as a Mac, so touch support tells the two apart.
  return /iPad|iPhone|iPod/.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);
};

type NativeMediaHandlers = {
    onPlaying: () => void;
    onWaiting: () => void;
    onEnded: (watchedSeconds: number, durationSeconds: number) => void;
    onError: (details: {code?: number; message?: string}) => void;
};

export const createNativeMediaPlayer = (
    media: HTMLMediaElement,
    url: string,
    handlers: NativeMediaHandlers
) => {
    media.src = url;

    const onPlaying = () => handlers.onPlaying();
    const onWaiting = () => handlers.onWaiting();
    const onEnded = () => handlers.onEnded(media.currentTime, media.duration);
    const onError = () => handlers.onError({code: media.error?.code, message: media.error?.message});

    media.addEventListener("playing", onPlaying);
    media.addEventListener("waiting", onWaiting);
    media.addEventListener("ended", onEnded);
    media.addEventListener("error", onError);

    return {
        play: () => media.play(),
        pause: () => media.pause(),
        seek: (time: number) => {
            media.currentTime = time;
            return true;
        },
        getCurrentTime: () => media.currentTime,
        getDuration: () => media.duration,
        isPlaying: () => !media.paused && !media.ended,
        destroy: () => {
            media.removeEventListener("playing", onPlaying);
            media.removeEventListener("waiting", onWaiting);
            media.removeEventListener("ended", onEnded);
            media.removeEventListener("error", onError);
            media.pause();
            // Without this the old stream keeps downloading after the user leaves
            media.removeAttribute("src");
            media.load();
            media.remove();
        },
    };
};
